<template>
  <WdsModal :show="show" :title="modalTitle" @cancel="$emit('cancel')">
    <WdsAlert info icon class="tw-mb-4">
      {{ $t('The following fields must be completed before sharing this job') }}
    </WdsAlert>

    <WdsStack direction-column gap-md>
      <WdsInput
        v-model="contactNameLocal"
        :placeholder="$t('Full name')"
        data-test="share-contact-name"
        :error="contactErrors.name"
      >
        <template #label>
          {{ $t('Contact name') }}
          <span class="tw-text-indigo-500 tw-font-bold">*</span>
        </template>
      </WdsInput>

      <WdsInput
        v-model="contactEmailLocal"
        :placeholder="$t('name@company.com')"
        data-test="share-contact-email"
        :error="contactErrors.email"
      >
        <template #label>
          {{ $t('Contact email') }}
          <span class="tw-text-indigo-500 tw-font-bold">*</span>
        </template>
      </WdsInput>

      <div>
        <WdsText primary class="tw-mb-1 tw-block">
          {{ $t('Contact phone') }}
          <span class="tw-text-indigo-500 tw-font-bold">*</span>
        </WdsText>
        <InputTelephone
          :key="`share-contact-phone-${phoneSeedKey}`"
          :persistent-phone-number="contactPhoneLocal"
          :show-verify-button="false"
          :invalid-phone-number-error="contactErrors.phone ?? ''"
          :placeholder="$t('+45 12 34 56 78')"
          data-test="share-contact-phone"
          @phone-changed="(value) => (contactPhoneLocal = value ?? '')"
        />
      </div>

      <WdsSpinner v-if="fieldsLoading" indeterminate />
      <FormCustomFields
        v-else-if="fields.length > 0"
        v-model:field-values="localFieldValues"
        :fields="fields"
        :loading="fieldsLoading"
        :force-enable-inputs="true"
        :errors="fieldErrors"
      />
    </WdsStack>

    <template #footer>
      <WdsStack justify-end gap-sm align-center>
        <WdsSpinner v-show="saving" :size="16" indeterminate />
        <WdsButton tertiary :disabled="saving" @click="$emit('cancel')">
          {{ $t('Cancel') }}
        </WdsButton>
        <WdsButton primary :disabled="saving || fieldsLoading" @click="handleSubmit">
          {{ $t('Save & Share') }}
        </WdsButton>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script setup lang="ts">
import { ref, computed, shallowRef, watch, Ref } from 'vue'
import {
  WdsAlert,
  WdsButton,
  WdsInput,
  WdsModal,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { useMutation, useQuery } from '@vue/apollo-composable'

import { useTranslation } from '@/composables/translation'
import { CustomFieldTypeValueInput, TypeSupportingCustomFieldValues } from '@/gql/graphql'
import { useFragment } from '@/gql'
import { NetworkError } from '@/utils/notification'
import { FormValidation } from '@/utils/formValidation'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'
import { isCustomFieldRequired } from '@/components/Fields/utils/CustomFieldUtils'
import {
  customFieldValueInputFieldId,
  diffCustomFieldValueInputs,
  hasFieldValue,
  mapCustomFieldValueToCustomFieldValueInput,
} from '@/components/Fieldable/utils'

import FormCustomFields from '@/components/Fieldable/FormCustomFields.vue'
import InputTelephone from '@/components/InputTelephone.vue'
import useSupplierSharedCustomFields from '@/components/Fieldable/composables/useSupplierSharedCustomFields'
import { FieldableFormCustomFieldFragment } from '@/components/Fieldable/graphql/fragments'
import { JobFormDataFragment } from '@/components/Job/graphql/fragments'
import { GetJobFormQuery } from '@/components/Job/graphql/queries'
import { UpdateJobMutation } from '@/components/Job/graphql/mutations'

const { $t } = useTranslation()

interface Props {
  show: boolean
  jobId: string
  accountId: string
  supplierId: string
  supplierName?: string
  contactName?: string | null
  contactEmail?: string | null
  contactPhone?: string | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  cancel: []
  completed: []
}>()

const { fields, loading: fieldsLoading } = useSupplierSharedCustomFields(
  props.accountId,
  ref(props.supplierId),
  [TypeSupportingCustomFieldValues.Job],
  PER_PAGE_MAXIMUM,
)

const { result: jobResult } = useQuery(
  GetJobFormQuery,
  { jobId: props.jobId },
  { clientId: 'publicApi' },
)

const localFieldValues = ref<CustomFieldTypeValueInput[]>([])
// Pristine baseline of the stored values, captured on each seed and kept
// apart from the editable state above.
const initialFieldValueInputs = ref<CustomFieldTypeValueInput[]>([])
const fieldErrors = shallowRef<Record<string, Ref<string>>>({})

const contactNameLocal = ref<string>(props.contactName ?? '')
const contactEmailLocal = ref<string>(props.contactEmail ?? '')
const contactPhoneLocal = ref<string>(props.contactPhone ?? '')
const contactErrors = ref<Record<string, string>>({})
// Bumped on each seed to remount the telephone input so it re-reads the
// seeded value (InputTelephone only reads persistent-phone-number on created()).
const phoneSeedKey = ref<number>(0)

// Seed once each time the modal opens, from the job's saved values (contact
// from props, custom-field values from the job query).
const seeded = ref<boolean>(false)

const seed = () => {
  contactNameLocal.value = props.contactName ?? ''
  contactEmailLocal.value = props.contactEmail ?? ''
  contactPhoneLocal.value = props.contactPhone ?? ''
  contactErrors.value = {}
  fieldErrors.value = {}

  const job = jobResult.value?.job ? useFragment(JobFormDataFragment, jobResult.value.job) : null
  localFieldValues.value = job?.customFieldValues?.length
    ? mapCustomFieldValueToCustomFieldValueInput(job.customFieldValues)
    : []
  // Mapped twice on purpose: the baseline must not share objects with the
  // editable state.
  initialFieldValueInputs.value = job?.customFieldValues?.length
    ? mapCustomFieldValueToCustomFieldValueInput(job.customFieldValues)
    : []

  phoneSeedKey.value += 1
  seeded.value = true
}

watch(
  () => props.show,
  (isShown) => {
    if (!isShown) {
      seeded.value = false
    } else if (jobResult.value?.job) {
      seed()
    }
  },
)

// If the job query resolves after the modal was opened, seed then.
watch(
  () => jobResult.value,
  () => {
    if (props.show && !seeded.value) {
      seed()
    }
  },
)

const { loading: saving, mutate: updateJob } = useMutation(UpdateJobMutation, {
  clientId: 'publicApi',
})

const modalTitle = computed(() =>
  props.supplierName ? $t('Share with :name', { name: props.supplierName }) : $t('Share job'),
)

const hasValueForField = (fieldId: string): boolean =>
  hasFieldValue(
    localFieldValues.value.find((fv) => customFieldValueInputFieldId(fv) === fieldId),
  )

const validateFields = (): boolean => {
  const errors: Record<string, Ref<string>> = {}

  fields.value
    .map((field) => useFragment(FieldableFormCustomFieldFragment, field))
    .filter((typed) => isCustomFieldRequired(typed.settings))
    .forEach((typed) => {
      if (!hasValueForField(typed.id)) {
        errors[typed.id] = ref($t('This field is required.'))
      }
    })

  fieldErrors.value = errors
  return Object.keys(errors).length === 0
}

const validateContact = (): boolean => {
  const errors: Record<string, string> = {}

  if (!contactNameLocal.value.trim()) {
    errors.name = $t('Contact name is required.')
  }

  const email = contactEmailLocal.value.trim()
  if (!email) {
    errors.email = $t('Contact email is required.')
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = $t('Please enter a valid email address.')
  }

  // InputTelephone emits an empty value for both a blank and an invalid number,
  // so this message covers "missing" and "not a valid number".
  if (!contactPhoneLocal.value.trim()) {
    errors.phone = $t('Please enter a valid phone number.')
  }

  contactErrors.value = errors
  return Object.keys(errors).length === 0
}

// Map server-side validation errors for the contact fields back onto the
// inputs (e.g. an invalid phone number). Returns true if any were applied.
const applyContactValidationErrors = (ex: any): boolean => {
  const validation = FormValidation.fromGraphqlErrors(ex?.graphQLErrors) as Record<
    string,
    string[]
  > | null
  if (!validation) return false

  const keyMap: Record<string, 'name' | 'email' | 'phone'> = {
    'input.contactName': 'name',
    'input.contactEmail': 'email',
    'input.contactPhone': 'phone',
  }

  const errors: Record<string, string> = {}
  Object.entries(validation).forEach(([key, messages]) => {
    const target = keyMap[key]
    if (target && messages?.length) {
      errors[target] = messages[0]
    }
  })

  if (Object.keys(errors).length === 0) return false

  contactErrors.value = { ...contactErrors.value, ...errors }
  return true
}

const handleSubmit = async () => {
  // Run both so all errors surface at once.
  const contactValid = validateContact()
  const fieldsValid = validateFields()
  if (!contactValid || !fieldsValid) return

  // Only the custom field values the user changed in this modal. Re-submitting
  // untouched fields deletes their stored values whenever the form holds them
  // as empty, so an unchanged form must leave the key out.
  const changedCustomFieldValues = diffCustomFieldValueInputs(
    localFieldValues.value,
    initialFieldValueInputs.value,
  )

  try {
    await updateJob(
      {
        input: {
          id: props.jobId,
          contactName: contactNameLocal.value.trim(),
          contactEmail: contactEmailLocal.value.trim(),
          contactPhone: contactPhoneLocal.value.trim(),
          ...(changedCustomFieldValues.length > 0
            ? { customFieldValues: changedCustomFieldValues }
            : {}),
        },
      },
      { context: { skipGlobalErrorHandling: true } },
    )

    emit('completed')
  } catch (ex: any) {
    if (applyContactValidationErrors(ex)) return

    new NetworkError({
      ex,
      msg: $t('An error occurred while saving the fields.'),
    }).dispatch()
  }
}
</script>
